import { useEffect, useState } from 'react';
import { Button, Icon, Input, Select } from '@repo/ui';

import {
  ArEmailGroupBySelectOption,
  ArEmailQueueFilter,
  ArEmailQueueItem,
} from '../../types/emailQueue';
import {
  ArClientType,
  ArClientTypeFilter,
  ArEmailAgingBucket,
  ArEmailGroupByOption,
  CLIENT_TYPE_FILTER_ALL,
} from '@repo/types';
import { agingBucketsByClientType, defaultAgingBucketByClientType } from './utils/agingBuckets';

type ActionToolbarProps = {
  isDataTableLoading: boolean;
  filters: ArEmailQueueFilter;
  onChange: (next: ArEmailQueueFilter) => void;
  options: ArEmailGroupBySelectOption[];
  selectedKeys: string[];
  itemsSelected: ArEmailQueueItem[];
  noEmailItem: ArEmailQueueItem[];
  isSending: boolean;
  onBulkRemove: (selectedKeys: string[]) => void;
  onSendEmail: (items: ArEmailQueueItem[]) => void;
};

function searchPlaceholderFor(groupBy: ArEmailGroupByOption): string {
  if (groupBy === ArEmailGroupByOption.NONE) {
    return 'Search matter, client, or attorney';
  }
  if (groupBy === ArEmailGroupByOption.CLIENT) {
    return 'Search client';
  }
  return 'Search attorney';
}

// ============================================================================
// Sub-components (internal to ActionsToolbarEmailQueue)
// ============================================================================

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  debounceMs?: number;
  disabled?: boolean;
}

function SearchInput({
  value,
  onChange,
  placeholder,
  debounceMs = 1000,
  disabled = false,
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    if (inputValue === value) return;

    const timeoutDebounced = setTimeout(() => {
      onChange(inputValue);
    }, debounceMs);

    return () => clearTimeout(timeoutDebounced);
  }, [inputValue, value, onChange, debounceMs]);

  return (
    <div className="relative flex-1 duration-300 max-w-xs">
      <Icon
        name="HiSearch"
        size="sm"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
      />
      <Input
        value={inputValue}
        variant="outlined"
        onChange={e => setInputValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="flex h-[38px] w-full px-3 py-2 pl-9 border border-xtnd-light dark:border-xtnd-dark focus:ring-offset-xtnd-blue focus:ring-transparent focus:border-xtnd-blue dark:focus:border-xtnd-blue"
      />
    </div>
  );
}

interface ClientTypeOption {
  value: ArClientTypeFilter;
  label: string;
}

const CLIENT_TYPE_OPTIONS: ClientTypeOption[] = [
  { label: 'All Client', value: CLIENT_TYPE_FILTER_ALL },
  { label: 'Private Client', value: ArClientType.PRIVATE },
  { label: 'Insurance Defense', value: ArClientType.INSURANCE },
];

interface EmailQueueFiltersProps {
  filters: ArEmailQueueFilter;
  onChange: (next: ArEmailQueueFilter) => void;
  disabled?: boolean;
}

function EmailQueueFilters({ filters, onChange, disabled = false }: EmailQueueFiltersProps) {
  const { clientType, agingBucket } = filters;

  const bucketOptions =
    clientType && clientType !== CLIENT_TYPE_FILTER_ALL
      ? agingBucketsByClientType[clientType].map(o => ({ value: o.value, label: o.label }))
      : [];

  return (
    <>
      <div className="min-w-[10.5rem] shrink-0">
        <Select<ClientTypeOption>
          fullWidth
          disabled={disabled}
          options={CLIENT_TYPE_OPTIONS}
          value={
            CLIENT_TYPE_OPTIONS.find(o => o.value === (clientType ?? CLIENT_TYPE_FILTER_ALL)) ??
            null
          }
          placeholder="Client Type"
          onChange={option => {
            if (!option) {
              onChange({
                ...filters,
                clientType: CLIENT_TYPE_FILTER_ALL,
                agingBucket: undefined,
              });
              return;
            }
            const nextValue =
              typeof option === 'string' ? (option as ArClientTypeFilter) : option.value;
            if (nextValue === CLIENT_TYPE_FILTER_ALL) {
              onChange({
                ...filters,
                clientType: CLIENT_TYPE_FILTER_ALL,
                agingBucket: undefined,
              });
              return;
            }
            onChange({
              ...filters,
              clientType: nextValue,
              agingBucket: defaultAgingBucketByClientType[nextValue],
            });
          }}
        />
      </div>
      {clientType && clientType !== CLIENT_TYPE_FILTER_ALL && (
        <div className="min-w-[7.5rem] shrink-0">
          <Select<{ value: ArEmailAgingBucket; label: string }>
            fullWidth
            disabled={disabled}
            options={bucketOptions}
            value={bucketOptions.find(o => o.value === agingBucket) ?? null}
            placeholder="Aging Bucket"
            onChange={option => {
              if (!option) return;
              const nextValue =
                typeof option === 'string' ? (option as ArEmailAgingBucket) : option.value;
              onChange({ ...filters, agingBucket: nextValue });
            }}
          />
        </div>
      )}
    </>
  );
}

interface EmailGroupBySelectProps {
  value: ArEmailGroupByOption;
  options: ArEmailGroupBySelectOption[];
  onChange: (v: ArEmailGroupByOption) => void;
  disabled?: boolean;
}

function EmailGroupBySelect({ value, options, onChange, disabled }: EmailGroupBySelectProps) {
  return (
    <Select<ArEmailGroupBySelectOption>
      className="w-60"
      options={options}
      value={options?.find(opt => opt?.value === value)}
      disabled={disabled}
      onChange={option => {
        if (!option) return;
        if (typeof option === 'string') {
          onChange(option as ArEmailGroupByOption);
          return;
        }
        onChange(option.value);
      }}
    />
  );
}

interface EmailQueueActionsProps {
  selectedKeys: string[];
  itemsSelected: ArEmailQueueItem[];
  noEmailItem: ArEmailQueueItem[];
  isSending: boolean;
  disabled?: boolean;
  onBulkRemove: (selectedKeys: string[]) => void;
  onSendEmail: (items: ArEmailQueueItem[]) => void;
}

export function EmailQueueActions({
  selectedKeys,
  itemsSelected,
  noEmailItem,
  isSending,
  disabled = false,
  onBulkRemove,
  onSendEmail,
}: EmailQueueActionsProps) {
  return (
    <div className="flex items-center">
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || selectedKeys.length === 0}
        leftIcon={
          <Icon
            name="FaTrash"
            fill="red"
            className="!h-4 !w-4 text-red-500 hover:text-red-600 cursor-pointer"
            title="Remove from email queue"
          />
        }
        className="!h-[38px] text-xtnd-dark hover:bg-xtnd-blue-600 border-xtnd-blue focus:ring-xtnd-blue-500 mr-4"
        onClick={() => onBulkRemove(selectedKeys)}
      >
        Bulk Delete ({itemsSelected.length})
      </Button>
      <Button
        variant="primary"
        size="sm"
        loading={isSending}
        disabled={disabled || itemsSelected.length === 0}
        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
          onSendEmail(itemsSelected)
        }
        leftIcon={<Icon name="FiSend" />}
        className="bg-xtnd-blue !h-[38px] text-xtnd-white hover:bg-xtnd-blue-600 border-xtnd-blue focus:ring-xtnd-blue-500"
      >
        Send Selected ({Math.max(0, (itemsSelected?.length ?? 0) - (noEmailItem?.length ?? 0))}
        {noEmailItem.length > 0 && itemsSelected ? `/${itemsSelected.length}` : ''})
      </Button>
    </div>
  );
}

// ============================================================================
// Top-level toolbar
// ============================================================================

export default function ActionsToolbarEmailQueue({
  isDataTableLoading,
  filters,
  onChange,
  options,
  selectedKeys,
  itemsSelected,
  noEmailItem,
  isSending,
  onBulkRemove,
  onSendEmail,
}: ActionToolbarProps) {
  const { search = '', groupBy } = filters;

  return (
    <div className="flex justify-between items-center w-full text-sm gap-4">
      <div className="flex items-center gap-4 flex-1">
        <SearchInput
          value={search}
          onChange={v => onChange({ ...filters, search: v })}
          placeholder={searchPlaceholderFor(groupBy)}
          disabled={isDataTableLoading}
        />
        {groupBy === ArEmailGroupByOption.NONE && (
          <EmailQueueFilters
            filters={filters}
            onChange={onChange}
            disabled={isDataTableLoading}
          />
        )}
        {selectedKeys.length > 0 && (
          <EmailQueueActions
            selectedKeys={selectedKeys}
            itemsSelected={itemsSelected}
            noEmailItem={noEmailItem}
            isSending={isSending}
            disabled={isDataTableLoading}
            onBulkRemove={onBulkRemove}
            onSendEmail={onSendEmail}
          />
        )}
      </div>
      <EmailGroupBySelect
        value={groupBy}
        options={options}
        onChange={v => onChange({ ...filters, groupBy: v })}
        disabled={isDataTableLoading}
      />
    </div>
  );
}
